import { Button } from '@mui/material';
import { Form, Formik, FormikHelpers, Field } from 'formik';
import { TextField } from 'formik-mui';
import { map, transform } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BankSelect,
  FormikDatePicker,
  FormikDateRange,
  FormikDateTimePicker,
  FormikMultiSelect,
  FormikNumericField,
  FormikRange,
  FormikSelect,
  MerchantSelect,
  OperatorSelect,
  OrderStatusSelect,
  ShopSelect,
  TraderSelect,
  UserSelect,
} from 'components';
import { DateFormat, FilterDefinitionType } from 'enums';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition } from 'types';
import { validationUtils } from 'utils';

type Props = {
  definitions: FilterDefinition[];
  initialValues: any;
  onReset: () => void;
  onSubmit: (values: any, helpers: FormikHelpers<any>) => void;
};

export const FiltersDropdownForm: React.FC<Props> = ({
  definitions,
  initialValues: initialValuesProp,
  onReset,
  onSubmit,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.filters_dropdown',
  });

  const getDefinitionDefaultValue = useCallback(
    (definition: FilterDefinition) =>
      // add conditions based on definitionType if needed (e.g. for number or date)
      {
        if (definition.type === FilterDefinitionType.MultiSelect) {
          return [];
        }

        return '';
      },
    [],
  );

  const initialValues = useMemo(
    () =>
      transform(
        definitions,
        (values: Record<string, any>, definition) => {
          if (definition.type === FilterDefinitionType.Range) {
            values[definition.minName] =
              initialValuesProp?.[definition.minName] ??
              getDefinitionDefaultValue(definition);
            values[definition.maxName] =
              initialValuesProp?.[definition.maxName] ??
              getDefinitionDefaultValue(definition);
          } else if (
            definition.type === FilterDefinitionType.DateRange ||
            definition.type === FilterDefinitionType.DateTimeRange
          ) {
            values[definition.from.name] =
              initialValuesProp?.[definition.from.name] ??
              getDefinitionDefaultValue(definition);
            values[definition.to.name] =
              initialValuesProp?.[definition.to.name] ??
              getDefinitionDefaultValue(definition);
          } else {
            values[definition.name] =
              initialValuesProp?.[definition.name] ??
              getDefinitionDefaultValue(definition);
          }
        },
        {},
      ),
    [definitions, initialValuesProp, getDefinitionDefaultValue],
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
    >
      <Form className="qqq">
        <div className="tw-grid tw-gap-2 tw-mt-4 tw-mb-8">
          {map(definitions, (filterDefinition: FilterDefinition) => {
            if (filterDefinition.type === FilterDefinitionType.Date) {
              return (
                <FormikDatePicker
                  key={filterDefinition.name}
                  label={filterDefinition.label}
                  name={filterDefinition.name}
                  startOf={filterDefinition.startOf}
                  endOf={filterDefinition.endOf}
                  minDate={filterDefinition.minDate}
                  maxDate={filterDefinition.maxDate}
                />
              );
            } else if (
              filterDefinition.type === FilterDefinitionType.DateTime
            ) {
              return (
                <FormikDateTimePicker
                  key={filterDefinition.name}
                  label={filterDefinition.label}
                  name={filterDefinition.name}
                  startOf={filterDefinition.startOf}
                  endOf={filterDefinition.endOf}
                  minDate={filterDefinition.minDate}
                  maxDate={filterDefinition.maxDate}
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.Text) {
              return (
                <Field
                  key={filterDefinition.name}
                  component={TextField}
                  label={filterDefinition.label}
                  type="text"
                  variant="standard"
                  name={filterDefinition.name}
                  validate={
                    filterDefinition.format &&
                    validationUtils.validators[filterDefinition.format]
                  }
                />
              );
            } else if (
              filterDefinition.type === FilterDefinitionType.TextArea
            ) {
              return (
                <Field
                  key={filterDefinition.name}
                  component={TextField}
                  label={filterDefinition.label}
                  type="text"
                  variant="standard"
                  name={filterDefinition.name}
                  multiline
                  rows={3}
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.Numeric) {
              return (
                <FormikNumericField
                  key={filterDefinition.name}
                  label={filterDefinition.label}
                  allowNegative={false}
                  variant="standard"
                  name={filterDefinition.name}
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.Range) {
              return (
                <FormikRange
                  key={`${filterDefinition.minName}-${filterDefinition.maxName}`}
                  label={filterDefinition.label}
                  minName={filterDefinition.minName}
                  maxName={filterDefinition.maxName}
                />
              );
            } else if (
              filterDefinition.type === FilterDefinitionType.DateRange ||
              filterDefinition.type === FilterDefinitionType.DateTimeRange
            ) {
              return (
                <FormikDateRange
                  key={filterDefinition.from.name + filterDefinition.to.name}
                  startOf={filterDefinition.startOf}
                  endOf={filterDefinition.endOf}
                  minDate={filterDefinition.minDate}
                  maxDate={filterDefinition.maxDate}
                  from={filterDefinition.from}
                  to={filterDefinition.to}
                  format={
                    filterDefinition.type === FilterDefinitionType.DateRange
                      ? DateFormat.Date
                      : DateFormat.Long
                  }
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.Enum) {
              return (
                <FormikSelect
                  key={filterDefinition.name}
                  label={filterDefinition.label}
                  name={filterDefinition.name}
                  variant="standard"
                  autocomplete={filterDefinition.autocomplete}
                  options={map(filterDefinition.enum, (value: string) => ({
                    value,
                    label: filterDefinition.getDisplayName?.(value) || value,
                  }))}
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.Select) {
              return (
                <FormikSelect
                  key={filterDefinition.name}
                  label={filterDefinition.label}
                  name={filterDefinition.name}
                  enableIdSearch={filterDefinition.enableIdSearch}
                  noneOption={filterDefinition.noneOption !== false}
                  variant="standard"
                  options={filterDefinition.options}
                  autocomplete={filterDefinition.autocomplete}
                />
              );
            } else if (
              filterDefinition.type === FilterDefinitionType.MultiSelect
            ) {
              return (
                <FormikMultiSelect
                  key={filterDefinition.name}
                  label={filterDefinition.label}
                  name={filterDefinition.name}
                  variant="standard"
                  options={filterDefinition.options}
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.User) {
              return (
                <UserSelect
                  key={filterDefinition.name}
                  name={filterDefinition.name}
                  label={filterDefinition.label}
                  users={filterDefinition.users}
                  autocomplete={filterDefinition.autocomplete}
                  variant="standard"
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.Shop) {
              return (
                <ShopSelect
                  key={filterDefinition.name}
                  name={filterDefinition.name}
                  label={filterDefinition.label}
                  shops={filterDefinition.shops}
                  autocomplete={filterDefinition.autocomplete}
                  variant="standard"
                />
              );
            } else if (
              filterDefinition.type === FilterDefinitionType.OrderStatus
            ) {
              return (
                <OrderStatusSelect
                  key={filterDefinition.name}
                  name={filterDefinition.name}
                  label={filterDefinition.label}
                  statuses={filterDefinition.orderStatuses}
                  autocomplete={filterDefinition.autocomplete}
                  variant="standard"
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.Trader) {
              return (
                <TraderSelect
                  key={filterDefinition.name}
                  name={filterDefinition.name}
                  label={filterDefinition.label}
                  traders={filterDefinition.traders}
                  getDisplayName={filterDefinition.getDisplayName}
                  autocomplete={filterDefinition.autocomplete}
                  variant="standard"
                />
              );
            } else if (
              filterDefinition.type === FilterDefinitionType.Merchant
            ) {
              return (
                <MerchantSelect
                  key={filterDefinition.name}
                  name={filterDefinition.name}
                  label={filterDefinition.label}
                  merchants={filterDefinition.merchants}
                  getDisplayName={filterDefinition.getDisplayName}
                  autocomplete={filterDefinition.autocomplete}
                  variant="standard"
                />
              );
            } else if (
              filterDefinition.type === FilterDefinitionType.Operator
            ) {
              return (
                <OperatorSelect
                  key={filterDefinition.name}
                  name={filterDefinition.name}
                  label={filterDefinition.label}
                  operators={filterDefinition.operators}
                  autocomplete={filterDefinition.autocomplete}
                  variant="standard"
                />
              );
            } else if (filterDefinition.type === FilterDefinitionType.Bank) {
              return (
                <BankSelect
                  key={filterDefinition.name}
                  name={filterDefinition.name}
                  label={filterDefinition.label}
                  banks={filterDefinition.banks}
                  autocomplete={filterDefinition.autocomplete}
                  variant="standard"
                />
              );
            }
            return null;
          })}
        </div>
        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={onReset}
          >
            {t('buttons.reset')}
          </Button>
          <Button variant="outlined" color="info" size="small" type="submit">
            {t('buttons.submit')}
          </Button>
        </div>
      </Form>
    </Formik>
  );
};
